import React, { createContext, useContext, useEffect, useState } from "react";
import { format, isSameDay } from "date-fns";
import { places as places } from "./places"

const BookingContext = createContext()

export const useBookingContext = () => {
    const context = useContext(BookingContext)
    if (context === undefined) {
        throw new Error("useBookingContext must be used within a BookingProvider");
    }
    return context;
}

export const BookingProvider = (props) => {
    const [loading, setLoading] = useState(false)
    const [idayvuelta, setIdayvuelta] = useState("iyv");
    const [date, setDate] = useState(undefined);
    const [range, setRange] = useState({ from: undefined, to: undefined })
    const [origin, setOrigin] = useState(places[0]);
    const [destinations, setDestinations] = useState(places[0].destinations)
    const [destination, setDestination] = useState(places[0].destinations[0]);
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    const [babies, setBabies] = useState(0);
    const [selectedOutboundTrip, setSelectedOutboundTrip] = useState()
    const [selectedReturnTrip, setSelectedReturnTrip] = useState()
    const [selectedBono, setSelectedBono] = useState()
    const [residente, setResidente] = useState(false)
    const [totals, setTotals] = useState({
        adultsTotal: 0,
        childrenTotal: 0,
        discount: 0,
        discountTotal: 0,
        total: 0
    });
    const [adultFare, setAdultFare] = useState(0)
    const [childFare, setChildFare] = useState(0)
    const [tripSearchResult, setTripSearchResult] = useState({
        outboundTrips: [],
        returnTrips: [],
        bonos: [],
        idayvuelta: "iyv",
        date: undefined,
        range: { from: undefined, to: undefined },
        origin: undefined,
        destination: undefined,
        adults: 0,
        children: 0,
        babies: 0
    })
    const [preBooking, setPreBooking] = useState()
    const [booking, setBooking] = useState()
    const [partnerId, setPartnerId] = useState()
    const [partnerDiscount, setPartnerDiscount] = useState(0)

    // Helper function to calculate fare based on trip type and resident status
    const calculateFare = (tarifa, fareType, tripType, isResident) => {
        const prefix = isResident ? 'res_' : '';
        const suffix = tripType === 'iyv' ? 'return' : 'oneway';
        return tarifa[`${prefix}${suffix}_${fareType}`];
    }

    //Set "adultFare" and "childFare" everytime "idayvuelta", !!"selectedOutboundTrip" or "residente" changes
    useEffect(() => {
        if (tripSearchResult.idayvuelta === "other" || tripSearchResult.idayvuelta === "bf") {
            if (!selectedBono) return
            setAdultFare(selectedBono.tarifa.return_adult)
            setChildFare(selectedBono.tarifa.return_child)
            return
        }
        if (!selectedOutboundTrip || !tripSearchResult) return

        setAdultFare(calculateFare(selectedOutboundTrip.details.tarifa, 'adult', tripSearchResult.idayvuelta, residente))
        setChildFare(calculateFare(selectedOutboundTrip.details.tarifa, 'child', tripSearchResult.idayvuelta, residente))
    }, [tripSearchResult, selectedOutboundTrip, selectedBono, residente])

    //Set "totals" everytime "adultFare", "childFare", "adults" or "children" changes
    useEffect(() => {
        if (!adultFare || !childFare || (!selectedOutboundTrip && !selectedBono) || !tripSearchResult) return;
        const adultsTotal = tripSearchResult.adults * adultFare
        const childrenTotal = tripSearchResult.children * childFare
        const routeDiscount = tripSearchResult.idayvuelta === "iyv" && selectedOutboundTrip && selectedReturnTrip && !residente
            ? !isSameDay(new Date(selectedOutboundTrip.date), new Date(selectedReturnTrip.date))
                ? 0
                : (selectedOutboundTrip?.details.discount || 0)
            : 0
        const discount = Math.min(100, Math.max(0, routeDiscount + partnerDiscount))
        const discountTotal = (adultsTotal + childrenTotal) * discount / 100;
        setTotals({
            adultsTotal,
            childrenTotal,
            discount,
            discountTotal,
            total: adultsTotal + childrenTotal - discountTotal
        })
    }, [adultFare, childFare, tripSearchResult, selectedOutboundTrip, selectedBono, residente, partnerDiscount]) //eslint-disable-line

    useEffect(() => {
        if (!preBooking) return;
        const adultsTotal = preBooking.adults * preBooking.tarifa_adult
        const childrenTotal = preBooking.children * preBooking.tarifa_child
        const discount = preBooking.discount || 0
        const discountTotal = (adultsTotal + childrenTotal) * discount / 100;
        setTotals({
            adultsTotal,
            childrenTotal,
            discount,
            discountTotal,
            total: adultsTotal + childrenTotal - discountTotal
        })
    }, [preBooking])

    useEffect(() => {
        const storedPartner = sessionStorage.getItem("partner")
        if (storedPartner) {
            setPartnerId(storedPartner)
        }

        const storedPartnerDiscount = Number(sessionStorage.getItem("partnerDiscount") || 0)
        if (!Number.isNaN(storedPartnerDiscount) && storedPartnerDiscount > 0) {
            setPartnerDiscount(Math.min(100, storedPartnerDiscount))
        }
    }, [])

    const webfind = async (host, language) => {
        setSelectedBono()
        setSelectedOutboundTrip()
        setSelectedReturnTrip()
        const outboundDate = date ? format(date, "y-MM-dd") : range.from ? format(range.from, "y-MM-dd") : undefined;
        const returnDate = range.to ? format(range.to, "y-MM-dd") : undefined
        const url = `${host}/webfind?origin=${origin.code}&destination=${destination.code}&lang=${language}&date=${outboundDate}${returnDate ? `&returndate=${returnDate}` : ""}${idayvuelta === "other" ? "&bonosonly=true" : idayvuelta === "bf" ? "&bonoformentera=true" : ""}&adults=${adults}&children=${children}&babies=${babies}${residente ? "&residente=true" : ""}`
        try {
            setLoading(true)
            const response = await fetch(url)
            if (response.status !== 200) return { errorCode: "bad-response" }
            const data = await response.json()
            if (data.errorCode === "OK") {
                if (window.fathom) {
                    window.fathom.trackEvent("web-trips-search"); // Track webfind goal
                }
                setTripSearchResult({
                    outboundTrips: data.outboundTrips,
                    returnTrips: data.returnTrips,
                    bonos: data.bonos?.filter(b => idayvuelta === "other" ? !b.formentera : b.formentera),
                    date,
                    range,
                    idayvuelta,
                    origin,
                    destination,
                    destinations,
                    adults,
                    children,
                    babies,
                    residente
                })
                sessionStorage.setItem("tripSearchResult", JSON.stringify({
                    outboundTrips: data.outboundTrips,
                    returnTrips: data.returnTrips,
                    bonos: data.bonos?.filter(b => idayvuelta === "other" ? !b.formentera : b.formentera),
                    date,
                    range,
                    idayvuelta,
                    origin,
                    destination,
                    destinations,
                    adults,
                    children,
                    babies,
                    residente
                }))
                return data
            } else {
                setTripSearchResult({
                    outboundTrips: data.outboundTrips,
                    returnTrips: data.returnTrips,
                    bonos: data.bonos?.filter(b => idayvuelta === "other" ? !b.formentera : b.formentera),
                    date,
                    range,
                    idayvuelta,
                    origin,
                    destination,
                    destinations,
                    adults,
                    children,
                    babies,
                    residente
                })
                sessionStorage.setItem("tripSearchResult", JSON.stringify({
                    outboundTrips: data.outboundTrips,
                    returnTrips: data.returnTrips,
                    bonos: data.bonos?.filter(b => idayvuelta === "other" ? !b.formentera : b.formentera),
                    date,
                    range,
                    idayvuelta,
                    origin,
                    destination,
                    destinations,
                    adults,
                    children,
                    babies,
                    residente
                }))
                return data
            }
        } catch (err) {
            throw new Error("Unexpected response from backend");
        } finally { setLoading(false) }
    }

    const restoreSessionStoredTripSearchResult = () => {
        const sessionStoredTripSearchResult = sessionStorage.getItem("tripSearchResult")
        if (!sessionStoredTripSearchResult) return false;
        else {
            const parsedSessionStoredTripResult = JSON.parse(sessionStoredTripSearchResult)
            //console.log("REstoring tripSearchResult", parsedSessionStoredTripResult)
            setTripSearchResult({
                outboundTrips: parsedSessionStoredTripResult.outboundTrips || [],
                returnTrips: parsedSessionStoredTripResult.returnTrips || [],
                bonos: parsedSessionStoredTripResult.bonos || [],
                idayvuelta: parsedSessionStoredTripResult.idayvuelta,
                date: parsedSessionStoredTripResult.date && new Date(parsedSessionStoredTripResult.date),
                range: parsedSessionStoredTripResult.range?.from ? { from: new Date(parsedSessionStoredTripResult.range.from), to: new Date(parsedSessionStoredTripResult.range.to || parsedSessionStoredTripResult.range.from) } : { from: undefined, to: undefined },
                origin: parsedSessionStoredTripResult.origin,
                destination: parsedSessionStoredTripResult.destination,
                destinations: parsedSessionStoredTripResult.destinations,
                adults: parsedSessionStoredTripResult.adults,
                children: parsedSessionStoredTripResult.children,
                babies: parsedSessionStoredTripResult.babies,
                excursion: parsedSessionStoredTripResult.excursion,
            })
            setResidente(parsedSessionStoredTripResult.residente)
            return true;
        }
    }

    const restoreSessionStoredSelectedTrips = () => {
        const sessionStoredSelectedTrips = sessionStorage.getItem("selectedTrips")
        if (!sessionStoredSelectedTrips) return false
        else {
            const parsedSessionStoredSelectedTrips = JSON.parse(sessionStoredSelectedTrips)
            //console.log("REstoring selectedTrips", parsedSessionStoredSelectedTrips)
            setSelectedOutboundTrip(parsedSessionStoredSelectedTrips.selectedOutboundTrip)
            setSelectedReturnTrip(parsedSessionStoredSelectedTrips.selectedReturnTrip)
            setSelectedBono(parsedSessionStoredSelectedTrips.selectedBono)
            return true
        }
    }

    return (
        <BookingContext.Provider
            value={
                {
                    loading,
                    setLoading,
                    idayvuelta,
                    setIdayvuelta,
                    date,
                    setDate,
                    range,
                    setRange,
                    origin,
                    setOrigin,
                    destinations,
                    destination,
                    setDestination,
                    setDestinations,
                    adults,
                    setAdults,
                    children,
                    setChildren,
                    babies,
                    setBabies,
                    selectedOutboundTrip,
                    setSelectedOutboundTrip,
                    selectedReturnTrip,
                    setSelectedReturnTrip,
                    selectedBono,
                    setSelectedBono,
                    residente,
                    setResidente,
                    totals,
                    adultFare,
                    childFare,
                    webfind,
                    tripSearchResult,
                    setTripSearchResult,
                    restoreSessionStoredTripSearchResult,
                    restoreSessionStoredSelectedTrips,
                    preBooking,
                    setPreBooking,
                    booking,
                    setBooking,
                    partnerId,
                    setPartnerId,
                    partnerDiscount,
                    setPartnerDiscount
                }
            }>
            {props.children}
        </BookingContext.Provider>
    );
};